import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import React, { useCallback, useState } from "react";
import { message } from "antd";
import { api } from "../../../api";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../../externalComponents/tailwind/dropdown";
import AutoreplyConflictModal from "../../../externalComponents/AutoreplyConflictModal";
import { useNavigate } from "react-router";

const ChatbotsActionsDropdown = ({ customerId, chatbot, setListData, automaticReplyNodeId, data, onAutomaticReplyNodeChange }) => {
  const navigate = useNavigate();
  const [anymessageModal, setAnymessageModal] = useState({
    isOpen: false,
    activeChatbot: null,
  });
  const [isConfirming, setIsConfirming] = useState(false);

  const handleArchiverClick = useCallback(() => {
    setListData((_listData) => ({
      ..._listData,
      isModalOpen: true,
      chatbotToChange: chatbot,
    }));
  }, [setListData, chatbot]);

  const handleDuplicateClick = useCallback(async () => {
    setListData((_listData) => ({
      ..._listData,
      chatbotToChange: chatbot,
      isModalDuplicateOpen: true,
      loadingRow: chatbot.id,
    }));
  }, [chatbot]);

  const handleStatusChange = useCallback(async () => {
    console.log("chatbot", chatbot);
    if (!chatbot.rootNode?.whatsappTrigger) {
      console.warn(
        "Cannot change status: rootNode or whatsappTrigger is missing",
        chatbot,
      );
      message.warning(
        "Cannot activate or deactivate the chatbot at the moment. Please try again later.",
      );
      return;
    }

    const active = chatbot.status === "active";

    if (chatbot.chatbotType === "autoreply" && !active) {
      const currentNodeId = automaticReplyNodeId;
      
      if (currentNodeId && currentNodeId !== chatbot.rootNode.id) {
        const currentScenario = await api.scenario.maybeFindFirst({
          filter: { rootNodeId: { equals: currentNodeId } },
          select: { id: true, name: true, rootNode: { whatsappTrigger: { id: true } } }
        });
        
        if (currentScenario) {
          setAnymessageModal({
            isOpen: true,
            activeChatbot: currentScenario,
          });
          return;
        }
      }
    }

    setListData((_listData) => ({
      ..._listData,
      chatbotToChange: chatbot,
      loadingRow: chatbot.id,
      isStatusLoading: true,
    }));
    
    try {
      await api.whatsappTrigger.update(chatbot.rootNode.whatsappTrigger.id, {
        status: active ? "inactive" : "active",
      });
      if (chatbot.chatbotType === "autoreply") {
        const newNodeId = active ? null : chatbot.rootNode.id;
        await api.customer.update(customerId, {
          automaticReplyNode: {
            _link: newNodeId,
          },
        });
        if (onAutomaticReplyNodeChange) {
          console.log('[handleStatusChange] Calling onAutomaticReplyNodeChange', newNodeId);
          onAutomaticReplyNodeChange(newNodeId);
        }
      }
      if (chatbot.chatbotType === "whatsapp_order") {
        await api.customer.update(customerId, {
          whatsappOrderNode: {
            _link: active ? null : chatbot.rootNode.id,
          },
        });
      }
      await api.scenario.update(chatbot.id, {
        status: active ? "inactive" : "active",
      });
      
      chatbot.status = active ? "inactive" : "active";
    } finally {
      setListData((_listData) => ({
        ..._listData,
        isStatusLoading: false,
      }));
    }
  }, [chatbot, automaticReplyNodeId, data, customerId, onAutomaticReplyNodeChange]);

  const handleAnymessageConfirm = async () => {
    setIsConfirming(true);
    setListData((_listData) => ({
      ..._listData,
      chatbotToChange: chatbot,
      loadingRow: chatbot.id,
      isStatusLoading: true,
    }));
    
    try {
      await api.switchAutoreplyChatbot({
        customerId,
        oldScenarioId: anymessageModal.activeChatbot.id,
        oldTriggerId: anymessageModal.activeChatbot.rootNode.whatsappTrigger.id,
        newScenarioId: chatbot.id,
        newTriggerId: chatbot.rootNode.whatsappTrigger.id,
        newRootNodeId: chatbot.rootNode.id,
      });
      
      message.success(`${chatbot.name} is now active`);
      
      chatbot.status = "active";
      const oldChatbot = data?.find(c => c.id === anymessageModal.activeChatbot.id);
      if (oldChatbot) {
        oldChatbot.status = "inactive";
      } 
      if (onAutomaticReplyNodeChange) {
        onAutomaticReplyNodeChange(chatbot.rootNode.id);
      }
    } catch (error) {
      message.error("Failed to activate chatbot. Please try again.");
      console.error("Error activating anymessage chatbot:", error);
    } finally {
      setAnymessageModal({ isOpen: false, activeChatbot: null });
      setIsConfirming(false);
      setListData((_listData) => ({
        ..._listData,
        isStatusLoading: false,
      }));
    }
  };

  const { status, id } = chatbot;
  return (
    <>
      <Dropdown>
        <DropdownButton plain aria-label="More options">
          <EllipsisHorizontalIcon />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownItem onClick={handleArchiverClick}>Archive</DropdownItem>
          <DropdownItem onClick={handleStatusChange}>
            {status === "active" ? "Turn-Off" : "Turn-On"}
          </DropdownItem>
          <DropdownItem onClick={handleDuplicateClick}>Duplicate</DropdownItem>
          <DropdownItem onClick={() => navigate(`/chatbots/edit/${id}`)}>
            Edit
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      
      <AutoreplyConflictModal
        isOpen={anymessageModal.isOpen}
        onClose={() => setAnymessageModal({ isOpen: false, activeChatbot: null })}
        onConfirm={handleAnymessageConfirm}
        activeChatbot={anymessageModal.activeChatbot}
        newChatbotName={chatbot.name}
        isConfirming={isConfirming}
      />
    </>
  );
};

export default ChatbotsActionsDropdown;
